import { faEllipsisVertical, faGlobe, faTags } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WidgetContainer } from '@frontend/app/refresh-components';
import { cn } from '@frontend/shadcn/lib/utils';
import React from 'react';
import { CircleDollarIcon, WalletIcon, UserGroupIcon } from '@revfluence/fresh-icons/solid/esm';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { largeNumberAbbreviatorWithDecimal } from '@frontend/utils';
import { useApolloClient } from '@frontend/applications/AffiliatesApp/hooks';
import {
  GET_OFFERS_BY_SEARCH_QUERY,
  GET_OFFER_AFFILIATES_QUERY,
  GET_OFFER_BY_ID_WITH_STATS_QUERY
} from '@frontend/applications/AffiliatesApp/queries';
import { GetOfferAffiliates } from '@frontend/applications/AffiliatesApp/queries/types/GetOfferAffiliates';
import { GetofferByIdWithStats } from '@frontend/applications/AffiliatesApp/queries/types/GetofferByIdWithStats';
import { ProjectWidgetContainer } from '../common/ProjectWidgetContainer';

interface SalesTrackingStatsProps {
  projectId?: number;
  className?: string;
}

// Aggregated data interface
interface SalesTrackingData {
  totalAffiliates: {
    count: number;
    withConversions: number;
  };
  conversions: {
    count: number;
    clicks: number;
  };
  affiliateSales: {
    amount: number;
    perMember: number;
  };
  commissions: {
    amount: number;
    avgPerMember: number;
  };
}

const SalesTrackingStatCard = ({
  title,
  value,
  subText,
  icon,
  tooltip,
  isLoading,
}: {
  title: string;
  value: string;
  subText: string;
  icon: React.ReactNode;
  tooltip?: string;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <ProjectWidgetContainer title={title} icon={icon} tooltip={tooltip} className="w-full">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <ProjectWidgetContainer title={title} icon={icon} tooltip={tooltip} className="w-full">
      <div className="flex flex-col justify-center items-start gap-2">
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          <div className="flex justify-start items-end gap-1">
            <div className="justify-start text-gray-800 text-3xl font-medium font-['Inter'] leading-[48px]">
              {value}
            </div>
          </div>
        </div>
        <div className="self-stretch inline-flex justify-start items-center gap-1">
          <div className="justify-center text-gray-500 text-xs font-normal font-['Inter'] leading-tight">{subText}</div>
        </div>
      </div>
    </ProjectWidgetContainer>
  );
};

// Function to aggregate data from detailed offer queries
const aggregateDetailedOffersData = (
  offersStats: GetofferByIdWithStats[],
  affiliatesData: GetOfferAffiliates[]
): SalesTrackingData => {
  let totalAffiliates = 0;
  let totalConversions = 0;
  let totalClicks = 0;
  let totalSales = 0;
  let totalCommissions = 0;
  let affiliatesWithConversions = 0;

  // Process stats from GET_OFFER_BY_ID_WITH_STATS_QUERY
  offersStats.forEach((offerStats) => {
    if (offerStats.offer) {
      // Add promo stats
      if (offerStats.offer.promoStats) {
        totalConversions += offerStats.offer.promoStats.conversions || 0;
        totalSales += offerStats.offer.promoStats.sales || 0;
        totalCommissions += offerStats.offer.promoStats.payoutEarned || 0;
      }

      // Add link stats
      if (offerStats.offer.linkStats) {
        totalConversions += offerStats.offer.linkStats.conversions || 0;
        totalClicks += offerStats.offer.linkStats.clicks || 0;
        totalSales += offerStats.offer.linkStats.sales || 0;
        totalCommissions += offerStats.offer.linkStats.payoutEarned || 0;
      }
    }
  });

  // Process affiliate data from GET_OFFER_AFFILIATES_QUERY
  affiliatesData.forEach((affiliateData) => {
    if (affiliateData.affiliates) {
      // Process promo affiliates
      affiliateData.affiliates.promoAffiliates?.forEach((affiliate) => {
        totalAffiliates++;
        if ((affiliate.conversions || 0) > 0) {
          affiliatesWithConversions++;
        }
      });

      // Process link affiliates stats
      affiliateData.affiliates.linkAffiliatesStats?.forEach((affiliate) => {
        totalAffiliates++;
        if ((affiliate.conversions || 0) > 0) {
          affiliatesWithConversions++;
        }
      });
    }
  });

  const avgSalesPerMember = totalAffiliates > 0 ? totalSales / totalAffiliates : 0;
  const avgCommissionsPerMember = totalAffiliates > 0 ? totalCommissions / totalAffiliates : 0;

  return {
    totalAffiliates: {
      count: totalAffiliates,
      withConversions: affiliatesWithConversions,
    },
    conversions: {
      count: totalConversions,
      clicks: totalClicks,
    },
    affiliateSales: {
      amount: totalSales,
      perMember: avgSalesPerMember,
    },
    commissions: {
      amount: totalCommissions,
      avgPerMember: avgCommissionsPerMember,
    },
  };
};

export const SalesTrackingStats = (props: SalesTrackingStatsProps) => {
  const { projectId, className } = props;
  const apolloClient = useApolloClient();

  // API state
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<SalesTrackingData | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setIsLoading(false);
      return;
    }

    const fetchDetailedOffersData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First, get offers for the project
        const { data: offersData } = await apolloClient.query({
          query: GET_OFFERS_BY_SEARCH_QUERY,
          variables: {
            query: {
              programId: projectId,
            },
          },
          fetchPolicy: 'network-only',
        });

        if (!offersData?.offers?.length) {
          setData({
            totalAffiliates: { count: 0, withConversions: 0 },
            conversions: { count: 0, clicks: 0 },
            affiliateSales: { amount: 0, perMember: 0 },
            commissions: { amount: 0, avgPerMember: 0 },
          });
          return;
        }

        const offerIds = offersData.offers.map((offer) => offer.id);

        // Fetch detailed stats for each offer
        const offersStatsPromises = offerIds.map((offerId) =>
          apolloClient.query({
            query: GET_OFFER_BY_ID_WITH_STATS_QUERY,
            variables: {
              id: offerId,
              // No startDate and endDate as requested
            },
            fetchPolicy: 'network-only',
          })
        );

        // Fetch affiliate data for each offer
        const affiliatesPromises = offerIds.map((offerId) => {
          // Find the offer to determine its type
          const offer = offersData.offers.find((o) => o.id === offerId);
          const isLinkOffer = offer?.type === 'LINK';
          const isPromoLinkOffer = offer?.isPromoLink || false;

          return apolloClient.query({
            query: GET_OFFER_AFFILIATES_QUERY,
            variables: {
              offerId,
              // No startDate and endDate as requested
              isLinkOffer,
              isPromoLinkOffer,
            },
            fetchPolicy: 'network-only',
          });
        });

        // Wait for all queries to complete
        const [offersStatsResults, affiliatesResults] = await Promise.all([
          Promise.all(offersStatsPromises),
          Promise.all(affiliatesPromises),
        ]);

        // Extract data from results
        const offersStats = offersStatsResults.map((result) => result.data);
        const affiliatesData = affiliatesResults.map((result) => result.data);

        // Aggregate the detailed data
        const aggregatedData = aggregateDetailedOffersData(offersStats, affiliatesData);
        setData(aggregatedData);
      } catch (err) {
        setError('Failed to load sales tracking data');
        setData({
          totalAffiliates: { count: 0, withConversions: 0 },
          conversions: { count: 0, clicks: 0 },
          affiliateSales: { amount: 0, perMember: 0 },
          commissions: { amount: 0, avgPerMember: 0 },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetailedOffersData();
  }, [projectId, apolloClient]);

  const formatCurrency = (amount: number) => {
    const abbreviated = largeNumberAbbreviatorWithDecimal(amount);
    return `$${abbreviated.value}${abbreviated.symbol}`;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (error) {
    return (
      <WidgetContainer
        widgetTitle="Sales Tracking"
        textColor="text-grey-6 text-sm"
        bgColor="bg-white"
        widgetIcon={<FontAwesomeIcon icon={faTags} className="text-grey-4" />}
        widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
        className={cn('h-min', className)}
      >
        <div className="p-4 text-center text-red-500">
          {error}
        </div>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer
      widgetTitle="Sales Tracking"
      textColor="text-grey-6 text-sm"
      bgColor="bg-white"
      widgetIcon={<FontAwesomeIcon icon={faTags} className="text-grey-4" />}
      widgetActions={<FontAwesomeIcon icon={faEllipsisVertical} className="text-grey-6" />}
      className={cn('h-min', className)}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4">
        <SalesTrackingStatCard
          title="Total Affiliates"
          value={data ? formatNumber(data.totalAffiliates.count) : '0'}
          subText={`${data?.totalAffiliates.withConversions || 0} with conversions`}
          icon={<UserGroupIcon className="w-4 h-4" />}
          tooltip="The number of members added across this offer. The metric below shows how many of them have driven at least one conversion."
          isLoading={isLoading}
        />
        <SalesTrackingStatCard
          title="Conversions"
          value={data ? formatNumber(data.conversions.count) : '0'}
          subText={`${data?.conversions.clicks || 0} clicks`}
          icon={<FontAwesomeIcon icon={faGlobe} className="w-4 h-4" />}
          tooltip="Shows the number of conversions driven by members across this offer. The metric below shows the number of clicks."
          isLoading={isLoading}
        />
        <SalesTrackingStatCard
          title="Affiliate Sales"
          value={data ? formatCurrency(data.affiliateSales.amount) : '$0'}
          subText={`$${data ? formatNumber(data.affiliateSales.perMember) : '0'} per member`}
          icon={<CircleDollarIcon className="w-4 h-4" />}
          tooltip="Shows the total sales driven by members across this offer. The metric below shows the average sales per member."
          isLoading={isLoading}
        />
        <SalesTrackingStatCard
          title="Commissions"
          value={data ? formatCurrency(data.commissions.amount) : '$0'}
          subText={`$${data ? formatNumber(data.commissions.avgPerMember) : '0'} avg. per member`}
          icon={<WalletIcon className="w-4 h-4" />}
          tooltip="Shows the total commissions earned by members across this offer. The metric below shows the average commission earned per member."
          isLoading={isLoading}
        />
      </div>
    </WidgetContainer>
  );
};
